import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { searchAPI } from '../services/api';

const DEBOUNCE_MS = 300;

export type GlobalSearchOption = {
  type: 'firm' | 'case' | 'project';
  id: number;
  label: string;
  sublabel?: string;
  firmId?: number;
};

const groupLabels: Record<GlobalSearchOption['type'], string> = {
  firm: 'Firms',
  case: 'Cases',
  project: 'Projects',
};

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<GlobalSearchOption[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(inputValue.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (query.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const runSearch = async () => {
      setLoading(true);
      try {
        const res = await searchAPI.global(query);
        if (cancelled) return;

        const data = res.data?.data || {};
        const nextOptions: GlobalSearchOption[] = [];

        (data.firms || []).forEach((firm: any) => {
          nextOptions.push({
            type: 'firm',
            id: firm.id,
            label: firm.name,
            sublabel: [firm.type, firm.city, firm.state].filter(Boolean).join(' · '),
            firmId: firm.id,
          });
        });

        (data.projects || []).forEach((project: any) => {
          nextOptions.push({
            type: 'project',
            id: project.id,
            label: project.name,
            sublabel: project.firm?.name || `Firm #${project.firm_id}`,
            firmId: project.firm_id,
          });
        });

        (data.cases || []).forEach((caseItem: any) => {
          nextOptions.push({
            type: 'case',
            id: caseItem.id,
            label: caseItem.title,
            sublabel: [
              caseItem.file_number,
              caseItem.project?.firm?.name,
              caseItem.status,
            ].filter(Boolean).join(' · '),
            firmId: caseItem.project?.firm_id,
          });
        });

        setOptions(nextOptions);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    runSearch();
    return () => {
      cancelled = true;
    };
  }, [query]);

  const handleSelect = (_event: React.SyntheticEvent, value: GlobalSearchOption | null) => {
    if (!value) return;

    if (value.type === 'firm' && value.firmId) {
      navigate(`/firms/${value.firmId}`);
    } else if (value.type === 'project' && value.firmId) {
      navigate(`/firms/${value.firmId}/projects/${value.id}`);
    } else if (value.type === 'case' && value.firmId) {
      navigate(`/firms/${value.firmId}/cases/${value.id}`);
    }

    setInputValue('');
    setOpen(false);
  };

  const noOptionsText = useMemo(() => {
    if (inputValue.trim().length < 2) return 'Type at least 2 characters';
    if (loading) return 'Searching...';
    return 'No results found';
  }, [inputValue, loading]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(_event, value) => setInputValue(value)}
      onChange={handleSelect}
      filterOptions={(x) => x}
      groupBy={(option) => groupLabels[option.type]}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.type === value.type && option.id === value.id}
      noOptionsText={noOptionsText}
      sx={{
        width: { xs: 160, sm: 240, md: 320 },
        mr: { xs: 0.5, sm: 1 },
        '& .MuiInputBase-root': {
          bgcolor: 'rgba(255,255,255,0.15)',
          color: 'inherit',
        },
        '& .MuiInputBase-input': {
          color: 'inherit',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255,255,255,0.3)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: 'rgba(255,255,255,0.5)',
        },
        '& .MuiSvgIcon-root': {
          color: 'inherit',
        },
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={`${option.type}-${option.id}`}>
          <Box>
            <Typography variant="body2">{option.label}</Typography>
            {option.sublabel && (
              <Typography variant="caption" color="text.secondary">
                {option.sublabel}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search..."
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default GlobalSearch;
